import React from 'react';
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import tw from "twin.macro";

import { Container } from "components/misc/Layouts.js";
import Hero from "components/hero/TwoColumnWithFeaturesAndTestimonial2.js";
import Footer from "components/footers/MiniCenteredFooter.js";

// Tailwind CSS is assumed to be available in the environment.
const HighlightedText = tw.span`text-primary-500`

const PrivacyPolicy = () => {
  return (
      <AnimationRevealPage>
    <Container>
      <Hero />

    <div className="consent container2">
      <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-lg p-6 sm:p-10">
        <header className="mb-8 border-b pb-4">
          <h1 className="text-3xl font-extrabold text-indigo-700 mb-2">
            Belle IT Privacy Policy
          </h1>
          <p className="text-sm text-gray-500">
            Effective Date: November 18, 2025
          </p>
          <p className="mt-4 text-gray-600">
            This Privacy Policy explains how Belle IT ("we," "us," or "our") collects, uses, and discloses information in connection with our general business operations and services.
          </p>
        </header>

        <section className="space-y-6 text-gray-700">

          {/* Section 1: Scope */}
          <div id="scope">
            <h2 className="text-2xl font-semibold text-gray-800 border-b pb-1 mb-3">
              1. Scope and Applicability
            </h2>
            <p>
              This policy covers **non-Protected Health Information (non-PHI)** collected from our website visitors, business contacts, and administrative users of our services (e.g., billing contacts, account managers).
            </p>
          </div>

          {/* Section 2: Information We Collect */}
          <div id="collection">
            <h2 className="text-2xl font-semibold text-gray-800 border-b pb-1 mb-3">
              2. Information We Collect
            </h2>
            <p>
              We collect information that identifies, relates to, describes, is reasonably capable of being associated with, or could reasonably be linked, directly or indirectly, with a particular consumer or household ("Personal Information").
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 mt-3">
              <li>
                <strong>Contact Data:</strong> Name, email address, phone number, postal address, and job title provided when you contact us or set up an administrative account.
              </li>
              <li>
                <strong>Billing Data:</strong> Information necessary to process payments, such as financial account numbers or credit card details (processed securely via third-party processors).
              </li>
              <li>
                <strong>Technical and Usage Data:</strong> IP address, browser type, operating system, pages visited on our website, and time spent using our administrative portals.
              </li>
            </ul>
          </div>

          {/* Section 3: How We Use Information */}
          <div id="use">
            <h2 className="text-2xl font-semibold text-gray-800 border-b pb-1 mb-3">
              3. How We Use the Information
            </h2>
            <p>We use Personal Information for the following business purposes:</p>
            <ul className="list-disc list-inside ml-4 space-y-2 mt-3">
              <li>To provide, maintain, and improve our specialized IT services.</li>
              <li>To process billing and payments related to our service contracts.</li>
              <li>To communicate with you regarding your account, security alerts, and service updates.</li>
              <li>For marketing and promotional purposes, subject to your consent (e.g., our SMS opt-in service).</li>
              <li>To ensure the security and stability of our systems and services.</li>
            </ul>
          </div>

          {/* Section 4: HIPAA and Protected Health Information (CRITICAL SECTION) */}
          <div id="hipaa" className="bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded-md">
            <h2 className="text-2xl font-extrabold text-indigo-700 border-b pb-2 mb-3">
              4. Protected Health Information (PHI) and HIPAA
            </h2>
            <p className="mb-2">
              Our core services involve handling **Protected Health Information (PHI)** on behalf of our Covered Entity clients, making Belle IT a **Business Associate** under the Health Insurance Portability and Accountability Act (HIPAA).
            </p>
            <p className="mb-2">
              **The handling of PHI is governed exclusively by the separate, fully executed Business Associate Agreement (BAA) between Belle IT and the Covered Entity client.** This general Privacy Policy does not apply to PHI.
            </p>
            <p>
              In the event of a conflict between this Policy and the BAA regarding the handling of PHI, the terms of the BAA shall control.
            </p>
          </div>

          {/* Section 5: Data Sharing */}
          <div id="sharing">
            <h2 className="text-2xl font-semibold text-gray-800 border-b pb-1 mb-3">
              5. Sharing and Disclosure
            </h2>
            <p>
              We may share your Personal Information with third parties only in the following circumstances:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 mt-3">
              <li>
                <strong>Service Providers:</strong> With third-party vendors and service providers who perform services for us, such as payment processing and data analytics, bound by confidentiality agreements.
              </li>
              <li>
                <strong>Legal Compliance:</strong> When required by law, subpoena, or other legal process, or if we believe that disclosure is necessary to protect our rights or the safety of others.
              </li>
            </ul>
          </div>
          
          {/* Section 6: Data Security */}
          <div id="security">
            <h2 className="text-2xl font-semibold text-gray-800 border-b pb-1 mb-3">
              6. Data Security
            </h2>
            <p>
              We implement reasonable and appropriate administrative, technical, and physical safeguards designed to protect the non-PHI Personal Information we collect from loss, misuse, and unauthorized access, disclosure, alteration, and destruction.
            </p>
          </div>

          {/* Section 7: Your Choices */}
          <div id="choices">
            <h2 className="text-2xl font-semibold text-gray-800 border-b pb-1 mb-3">
              7. Your Choices and Rights
            </h2>
            <p>
              You have the right to request access to or correction of the Personal Information we hold about you. You may also opt-out of receiving promotional communications from us by following the unsubscribe instructions included in those communications.
            </p>
          </div>

          <footer className="pt-6 border-t mt-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-3">
                Contact Us
            </h2>
            <p className="text-sm text-gray-500">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <p className="text-sm text-gray-500 mt-2">
              <strong>Belle IT</strong><br />
              Email: <a href="mailto:privacy@belleit.net" className="text-indigo-600 hover:underline">privacy@belleit.net</a><br />
              Address: [Belle IT Legal Department Address]
            </p>
          </footer>
        </section>
      </div>
    </div>
       </Container>
       <Footer />
     
     </AnimationRevealPage>
  );
};

export default PrivacyPolicy;